import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { Policy, PolicyPrice } from '../../policies/models/Policy';
import { Property } from '../../properties/models/Property';
import { GetPropertyResponse } from '../models/GetPropertyResponse';
import { CreatePropertyRequest } from '../property-form/models/CreatePropertyRequest.model';
import { Logger } from '../../../infrastructure/logging/logger';
import { SearchParams } from '../../../shared/search/SearchParams';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';
import { NotificationService } from '../../../utilities/PortalNotificationUtility';

@Injectable({
  providedIn: 'root'
})
export class PropertyApiService {

  private cachedPolicies: Policy[] = [];

  lastSearchRequest?: SearchParams;

  constructor(
    private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar,
    private logService: Logger,
    private notificationService: NotificationService
  ) { }

  searchProperties = async (searchRequest: SearchParams, disableRequestCache?: boolean): Promise<GetPropertyResponse> => {
    try {
      if (!disableRequestCache) {
        this.lastSearchRequest = searchRequest;
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/properties/search', { ...searchRequest }));
      if (!response) {
        return Promise.reject(`Search properties response null`);
      }

      return Promise.resolve({
        properties: response.data,
        totalRecords: response.metaData.totalRecords
      });
    } catch (e) {
      return Promise.reject(`Error while searching Properties: ${JSON.stringify(e)}`);
    }
  }

  async getProperties(): Promise<Property[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/properties`));
      if (!response) {
        return Promise.reject('Get properties response null');
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving properties ${e}`);
    }
  }

  async getPropertiesByOrganizationId(organization_id?: number): Promise<Property[]> {
    const params: any = {
      organization_id: organization_id
    };
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/properties`, { params }));
      if (!response) {
        return Promise.reject('Get properties response null');
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving properties ${e}`);
    }
  }

  getPropertyDetail = async (propertyId: number): Promise<Property> => {
    const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/properties/${propertyId}`));
    return Promise.resolve(response?.data);
  }

  getPropertyPolicies = async (propertyId?: number): Promise<Policy[]> => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/properties/${propertyId}/policies`));
      return Promise.resolve(response.data);
    } catch (e) {
      this.logService.handleError(e, `Error while retrieving Property policies for Property ID: ${propertyId}`, `Error while retrieving Property policies for Property ID: ${propertyId}`);
      return Promise.reject(e);
    }
  }

  getAvailablePropertyPolicies = async (organizationId: number, propertyId?: number, numberOfNights?: number, numberOfGuests?: number, reservationCost?: number): Promise<Policy[] | undefined> => {
    try {
      const params: any = {
        property_id: propertyId,
        organization_id: organizationId
      };

      if (numberOfNights) {
        params.number_of_nights = numberOfNights;
      }

      if (numberOfGuests) {
        params.number_of_guests = numberOfGuests;
      }

      if (reservationCost) {
        params.reservation_cost = reservationCost;
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/policies/available`, { params }));
      return response.data;
    } catch (e) {
      return Promise.reject(`Error while retrieving available policies for property ${propertyId} ${JSON.stringify(e)}`);
    }
  }

  updatePropertyStatus = async (property: Property): Promise<Property[]> => {
    const propertyId = property.property_id;

    const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/properties/${propertyId}/update-status`, property));
    return Promise.resolve(response.data);
  }

  getOrganizationPolicyPriceDetail = async (policyPriceId: number, organizationId: number | string): Promise<PolicyPrice | undefined> => {
    const policies = await this.getPoliciesByOrganizationId(organizationId);
    return policies.find(policy => policy.policy_price_id == policyPriceId)?.policy_price;
  }

  getPoliciesByOrganizationId = async (organizationId: number | string): Promise<Policy[]> => {
    if (this.cachedPolicies.length == 0) {
      try {
        const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/organizations/policies/query?organization_id=${organizationId}`));
        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(`Error while retrieving organization details for id ${organizationId} ${JSON.stringify(e)}`);
      }
    }

    return Promise.resolve(this.cachedPolicies);
  }

  async getPropertyPolicyPriceDetail(policyPriceId: string): Promise<PolicyPrice> {
    const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/properties/policy-prices/${policyPriceId}`));
    return Promise.resolve(response.data);
  }

  createProperty = async (request: CreatePropertyRequest): Promise<Property> => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/properties`, request));
      this.displaySnackbar(response.err);
      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating property ${JSON.stringify(e)}`);
    }
  }

  updateProperty = async (propertyId: number, request: CreatePropertyRequest): Promise<Property> => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/properties/${propertyId}`, request));
      if (!response) {
        return Promise.reject(`Update property response null ${propertyId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating property ${propertyId} ${JSON.stringify(e)}`);
    }
  }

  async getPartnerOrgPropertyPolicyCountByOrganizationId(organizationId?: string): Promise<any> {
    if (!organizationId) {
      this.notificationService.showNotification({ message: 'Null or undefined Organization ID'});
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/policies/properties/v2/${organizationId}`));
      if (response != null) {
        return Promise.resolve(response);
      } else {
        return Promise.reject(`Get property policies response null for Partner Org Id: ${organizationId}`);
      }
    } catch (e) {
      return Promise.reject(`Error while retrieving property policies for Partner Org Id: ${organizationId} ${JSON.stringify(e)}`);
    }
  }


  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }

  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Property: ${errorMessage}`, 'OK', { duration: 4000 });
  }

  displayStatusSuccessSnackbar(propertyId: number) {
    this.snackbar.open(`Property #${propertyId} - Status Update Successful`, 'OK', { duration: 4000 });
  }
}